export interface Feature {
  icon: string;
  title: string;
  description: string;
  category: FeatureCategoryId;
  badge?: string;
  /** Shown in the homepage features grid */
  onHome?: boolean;
}

export type FeatureCategoryId = 'properties' | 'agreements' | 'payments' | 'operations';

export const featureCategories: { id: FeatureCategoryId; label: string; icon: string; blurb: string }[] = [
  {
    id: 'properties',
    label: 'Properties & Tenants',
    icon: 'fas fa-building',
    blurb: 'Organize every property, unit, and tenant in one place.'
  },
  {
    id: 'agreements',
    label: 'Agreements & Documents',
    icon: 'fas fa-file-signature',
    blurb: 'Create, share, and store leases without the paperwork.'
  },
  {
    id: 'payments',
    label: 'Payments & Insights',
    icon: 'fas fa-wallet',
    blurb: 'Know who paid, who is due, and how your rentals perform.'
  },
  {
    id: 'operations',
    label: 'Maintenance & Operations',
    icon: 'fas fa-screwdriver-wrench',
    blurb: 'Keep repairs, inventory, and reminders under control.'
  }
];

export const features: Feature[] = [
  {
    icon: 'fas fa-tachometer-alt',
    title: 'Smart Dashboard',
    category: 'payments',
    onHome: true,
    description: 'See everything at a glance — unpaid and upcoming rent due in 7 days, monthly collections chart with year-over-year tracking, expiring agreement alerts, and a full activity feed.'
  },
  {
    icon: 'fas fa-wand-magic-sparkles',
    title: 'Guided Setup',
    category: 'properties',
    description: 'A step-by-step setup wizard walks you through adding your first property, unit, tenant, and rental agreement — you are ready to collect rent in minutes.'
  },
  {
    icon: 'fas fa-home',
    title: 'Property Management',
    category: 'properties',
    onHome: true,
    description: 'Add and manage single or multi-unit properties across 6 types: House, Apartment, Condominium, Retail, Office, and Parking. Track amenities, bedrooms, bathrooms, photos, and view location on Google Maps.'
  },
  {
    icon: 'fas fa-building',
    title: 'Unit Management',
    category: 'properties',
    onHome: true,
    description: 'For multi-unit buildings, manage individual units with their own floor number, rent, amenities, and images. Assign tenants per unit and track occupancy across your entire building.'
  },
  {
    icon: 'fas fa-boxes',
    title: 'Property Inventory',
    category: 'properties',
    onHome: true,
    description: 'Track furniture, appliances, fixtures, and electronics room by room — with condition status (new, excellent, good, fair, poor, broken, missing). Export the full inventory as a PDF checklist.'
  },
  {
    icon: 'fas fa-users',
    title: 'Tenant Management',
    category: 'properties',
    onHome: true,
    description: 'Store complete tenant profiles — personal details, emergency contacts, occupation, nationality, and communication preferences. Call or message tenants directly from the app.'
  },
  {
    icon: 'fas fa-people-roof',
    title: 'Occupants',
    category: 'properties',
    description: 'Record everyone living in a unit under each tenant — spouse, children, parents, relatives, or friends — so you always know who stays at your property.'
  },
  {
    icon: 'fas fa-file-contract',
    title: 'Rental Agreements',
    category: 'agreements',
    onHome: true,
    description: 'Create lease agreements with custom terms, start/end dates, rent, security deposit, and daily, monthly, or yearly billing. Include move-in and move-out checklists, share a link so tenants can view online, and archive old agreements to keep a full history.'
  },
  {
    icon: 'fas fa-robot',
    title: 'AI Agreement Drafts',
    category: 'agreements',
    badge: 'AI-powered',
    onHome: true,
    description: 'Generate a complete rental agreement in minutes using AI. Customize clauses to fit your property and tenant, then export as a professional PDF ready to sign.'
  },
  {
    icon: 'fas fa-link',
    title: 'Tenant Access Link',
    category: 'agreements',
    description: 'Share a unique link so tenants can view their rental agreement online — no app required on their end.'
  },
  {
    icon: 'fas fa-folder-open',
    title: 'Document Storage',
    category: 'agreements',
    onHome: true,
    description: 'Store and organize property and agreement documents securely via Google Drive integration. All files are validated, sanitized, and always accessible from the app.'
  },
  {
    icon: 'fas fa-file-invoice-dollar',
    title: 'Payments & Billing',
    category: 'payments',
    onHome: true,
    description: 'Track rent, deposits, late fees, and utilities as paid, pending, overdue, or cancelled. Supports cash, bank transfer, e-wallets, cards, and checks — with reference numbers and proof-of-payment photos. Generates PDF receipts with QR codes and emails them to tenants.'
  },
  {
    icon: 'fas fa-repeat',
    title: 'Recurring Charges',
    category: 'payments',
    onHome: true,
    description: 'Set up repeating charges on any agreement — utilities, electricity, water, internet, association or HOA dues, parking, and garbage collection — with automatic due-date reminders.'
  },
  {
    icon: 'fas fa-wrench',
    title: 'Maintenance Requests',
    category: 'operations',
    onHome: true,
    description: 'Log repair issues with photos, a description, category (plumbing, electrical, HVAC, appliance, structural, pest control, cleaning, and more), and priority (low, medium, high, emergency). Track status from pending to in progress to completed.'
  },
  {
    icon: 'fas fa-bell',
    title: 'Smart Notifications',
    category: 'operations',
    description: 'Get push reminders when rent and recurring charges are due, alerts for agreements expiring within 60 days, and activity notifications for all key changes.'
  },
  {
    icon: 'fas fa-comments',
    title: 'Rental Assistant',
    category: 'operations',
    description: 'Ask quick questions right from your dashboard — which rentals are due today, how many properties are available, and which agreements are unpaid or expiring soon.'
  },
  {
    icon: 'fas fa-chart-bar',
    title: 'Reports',
    category: 'payments',
    onHome: true,
    description: 'Generate rent collection reports with summaries and trends. Track revenue performance across all your properties and download reports as PDFs for accounting and records.'
  }
];

export const homeFeatures = features.filter((feature) => feature.onHome);
